import { TextInput, Text, Badge, Skeleton, Checkbox, Stack, UnstyledButton, Group, Tooltip, ActionIcon } from '@mantine/core';
import { Search, ChevronRight, ChevronDown, PanelLeftClose } from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';
import type { DataLibraryCohortGroup } from '../../types';
import { getCohortStatusColor, getCohortStatusLabel } from '../../config/statusConfig';
import type { CohortStatus } from '../../types';
import classes from './DataLibraryDrawer.module.css';

interface DataLibrarySidePanelProps {
  cohortGroups: DataLibraryCohortGroup[];
  selectedCohortIds: string[];
  onCohortToggle: (cohortId: string) => void;
  onSelectAll: (cohortIds: string[]) => void;
  loading: boolean;
  onCollapse?: () => void;
}

export function DataLibrarySidePanel({
  cohortGroups,
  selectedCohortIds,
  onCohortToggle,
  onSelectAll,
  loading,
  onCollapse,
}: DataLibrarySidePanelProps) {
  const [search, setSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return cohortGroups;
    const term = search.toLowerCase();
    return cohortGroups
      .map(group => ({
        ...group,
        cohorts: group.cohorts.filter(
          c =>
            c.name.toLowerCase().includes(term) ||
            group.partner_name.toLowerCase().includes(term),
        ),
      }))
      .filter(group => group.cohorts.length > 0);
  }, [cohortGroups, search]);

  // Get all live cohort IDs for "All Live" button
  const allLiveCohortIds = useMemo(
    () =>
      cohortGroups.flatMap(g => g.cohorts.filter(c => c.status === 'live').map(c => c.id)),
    [cohortGroups],
  );

  const allLiveSelected = useMemo(
    () => allLiveCohortIds.length > 0 && allLiveCohortIds.every(id => selectedCohortIds.includes(id)),
    [allLiveCohortIds, selectedCohortIds],
  );

  const someLiveSelected = useMemo(
    () =>
      !allLiveSelected &&
      allLiveCohortIds.some(id => selectedCohortIds.includes(id)),
    [allLiveCohortIds, selectedCohortIds, allLiveSelected],
  );

  const handleAllLiveToggle = useCallback(() => {
    onSelectAll(allLiveCohortIds);
  }, [allLiveCohortIds, onSelectAll]);

  const toggleGroup = useCallback((partnerId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(partnerId)) {
        next.delete(partnerId);
      } else {
        next.add(partnerId);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className={classes.sidePanel}>
        <div className={classes.sidePanelHeader}>
          <Text fw={600} size="sm" mb="xs">
            Programs & Cohorts
          </Text>
          <Skeleton height={32} radius="sm" />
        </div>
        <Stack gap="xs" p="md">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={24} radius="sm" />
          ))}
        </Stack>
      </div>
    );
  }

  return (
    <div className={classes.sidePanel}>
      <div className={classes.sidePanelHeader}>
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="sm">
            Programs & Cohorts
          </Text>
          {onCollapse && (
            <Tooltip label="Collapse sidebar" position="right" withArrow>
              <ActionIcon variant="subtle" color="gray" size="sm" onClick={onCollapse} aria-label="Collapse sidebar">
                <PanelLeftClose size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
        <TextInput
          placeholder="Search cohorts..."
          size="xs"
          leftSection={<Search size={14} />}
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
        />
      </div>

      {/* All Live quick-select */}
      {allLiveCohortIds.length > 0 && (
        <div
          className={`${classes.cohortItem} ${allLiveSelected ? classes.cohortItemSelected : ''}`}
          onClick={handleAllLiveToggle}
          style={{ borderBottom: '1px solid #e8e6e1' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Checkbox
              size="xs"
              checked={allLiveSelected}
              indeterminate={someLiveSelected}
              onChange={handleAllLiveToggle}
              onClick={e => e.stopPropagation()}
            />
            <Text size="sm" fw={600}>
              All Live
            </Text>
          </div>
          <Badge
            size="xs"
            color="medleyGreen"
            variant="light"
            styles={{ label: { fontSize: 9 } }}
          >
            {allLiveCohortIds.length}
          </Badge>
        </div>
      )}

      {filteredGroups.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" p="md">
          No cohorts found
        </Text>
      ) : (
        filteredGroups.map(group => {
          const groupCohortIds = group.cohorts.map(c => c.id);
          const allSelected = groupCohortIds.every(id => selectedCohortIds.includes(id));
          const someSelected =
            !allSelected && groupCohortIds.some(id => selectedCohortIds.includes(id));
          const isCollapsed = collapsedGroups.has(group.partner_id);

          return (
            <div key={group.partner_id} className={classes.partnerGroup}>
              <UnstyledButton
                className={classes.partnerName}
                onClick={() => toggleGroup(group.partner_id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}
              >
                {isCollapsed ? (
                  <ChevronRight size={12} color="#888" />
                ) : (
                  <ChevronDown size={12} color="#888" />
                )}
                <Checkbox
                  size="xs"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => onSelectAll(groupCohortIds)}
                  onClick={e => e.stopPropagation()}
                  aria-label={`Select all ${group.partner_name} cohorts`}
                />
                <Group gap={4} style={{ flex: 1 }} justify="space-between">
                  <span>{group.partner_name}</span>
                  <span className={classes.cohortCount}>{group.cohorts.length}</span>
                </Group>
              </UnstyledButton>
              {!isCollapsed &&
                group.cohorts.map(cohort => (
                  <div
                    key={cohort.id}
                    className={`${classes.cohortItem} ${selectedCohortIds.includes(cohort.id) ? classes.cohortItemSelected : ''}`}
                    onClick={() => onCohortToggle(cohort.id)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Checkbox
                        size="xs"
                        checked={selectedCohortIds.includes(cohort.id)}
                        onChange={() => onCohortToggle(cohort.id)}
                        onClick={e => e.stopPropagation()}
                      />
                      <Tooltip label={cohort.name} openDelay={300} position="right" withinPortal>
                        <span className={classes.cohortName}>{cohort.name}</span>
                      </Tooltip>
                    </div>
                    <Badge
                      size="xs"
                      style={{ flexShrink: 0 }}
                      color={getCohortStatusColor(cohort.status as CohortStatus)}
                      variant="filled"
                      tt="uppercase"
                      styles={{ label: { fontSize: 9, letterSpacing: 0.3 } }}
                    >
                      {getCohortStatusLabel(cohort.status as CohortStatus)}
                    </Badge>
                  </div>
                ))}
            </div>
          );
        })
      )}
    </div>
  );
}
